'use client';

// SuggestedQuestions - Display suggested questions as clickable chips with emoji icons

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useTranslations } from 'next-intl';
import { getQuestionEmoji } from './questionEmojis';

export interface SuggestedQuestionsProps {
  /** Suggested questions (exactly 3) */
  suggestions: string[];
  /** Whether suggestions are loading */
  isLoading?: boolean;
  /** Callback when a suggestion is clicked */
  onSelect: (question: string) => void;
  /** List of questions that have been used (clicked) */
  usedQuestions?: string[];
  /** Whether to hide completely (e.g., when all suggestions used) */
  hidden?: boolean;
}

/**
 * SuggestedQuestions - Display suggested questions as clickable chips
 *
 * Features:
 * - Exactly 3 questions displayed as chips with emoji icons
 * - White background with indigo border, turns indigo on hover
 * - Used questions shown grayed out
 * - Horizontally scrollable on mobile
 * - Clickable to send question immediately
 * - Loading skeleton state
 * - Hidden after all suggestions used
 */
export function SuggestedQuestions({
  suggestions,
  isLoading = false,
  onSelect,
  usedQuestions = [],
  hidden = false,
}: SuggestedQuestionsProps) {
  const t = useTranslations('creator.testing');

  // Hide if explicitly hidden
  if (hidden) {
    return null;
  }

  // Loading state - show skeletons even if suggestions array is empty
  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('suggestedQuestions.loading')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={150 + i * 20}
              height={32}
              sx={{ borderRadius: 4 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Empty state - return null when no suggestions to display
  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1,
          fontWeight: { xs: 600, md: 400 },
          fontSize: { xs: '0.9rem', md: '0.875rem' },
        }}
      >
        {t('suggestedQuestions.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1.5, md: 1 },
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
        }}
      >
        {suggestions.map((question) => {
          const isUsed = usedQuestions.includes(question);
          const emoji = getQuestionEmoji(question);

          return (
            <Chip
              key={question}
              label={`${emoji} ${question}`}
              onClick={() => !isUsed && onSelect(question)}
              disabled={isUsed}
              sx={{
                backgroundColor: isUsed ? 'grey.100' : 'white',
                border: '1px solid',
                borderColor: isUsed ? 'grey.300' : 'primary.main',
                color: isUsed ? 'grey.500' : 'primary.main',
                cursor: isUsed ? 'default' : 'pointer',
                flexShrink: 0,
                // Mobile: auto-grow height for wrapped text; desktop: fixed compact height
                height: { xs: 'auto', md: 32 },
                minHeight: { xs: 44, md: 32 },
                py: { xs: 1, md: 0 },
                fontSize: { xs: '0.95rem', md: '0.8125rem' },
                justifyContent: { xs: 'flex-start', md: 'center' },
                px: { xs: 1, md: 0 },
                '& .MuiChip-label': {
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  wordBreak: 'break-word',
                  px: { xs: 1.5, md: 1 },
                  py: { xs: 0.5, md: 0 },
                  display: 'block',
                },
                '&:hover': {
                  backgroundColor: isUsed ? 'grey.100' : 'primary.main',
                  color: isUsed ? 'grey.500' : 'white',
                },
                '&.Mui-disabled': {
                  opacity: 0.7,
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default SuggestedQuestions;
